import { useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type { ReferenceMasterKind } from '@/lib/api';
import { StageSwatch } from '@/components/StageSwatch';
import { Button, Checkbox, Combobox, Field, Input, Modal } from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { renderInclusion } from '@/lib/inclusions';
import { useReferenceOptions, withStoredOption } from '@/lib/use-reference-options';
import type {
  CampaignInclusion,
  CampaignInclusionCreateInput,
  CampaignInclusionUpdateInput,
  City,
  CityCreateInput,
  CityUpdateInput,
  Country,
  CountryCreateInput,
  CountryUpdateInput,
  Currency,
  CurrencyCreateInput,
  CurrencyUpdateInput,
  DealStage,
  DealStageCreateInput,
  DealStageUpdateInput,
  DealType,
  DealTypeCreateInput,
  DealTypeUpdateInput,
  LeadSource,
  MasterRef,
  Product,
  ReferenceCreateInput,
  ReferenceRecord,
  ReferenceUpdateInput,
} from '@/lib/types';

/*
 * The create/edit form behind every reference master on the catalog screen.
 *
 * Six masters, three payload shapes, one form. They differ in which keys they
 * carry, not in how they behave: all six are `.strict()` schemas where an
 * unknown key is a 422 rather than being ignored, all six treat an explicit
 * `null` on a NOT NULL column as a 422, and every one of them has a UNIQUE
 * column that answers a duplicate with a 409 — on update as well as create.
 * Those three facts drive most of the code below.
 *
 * What this form deliberately does NOT offer is a delete: deleting is the
 * demoted action and lives on the list beside each row, behind its own
 * confirmation. See `CatalogPage`.
 */

export type MasterKind =
  | 'regions'
  | 'bookingStatuses'
  | 'travelPackages'
  | 'adsPlans'
  | 'currencies'
  // Countries: writable (create + update), but NOT delete.
  | 'countries'
  /*
   * Cities are a seventh master and a genuinely different shape: two picklist
   * FOREIGN KEYS on one form, where every other master here carries only scalar
   * columns. `docs/api/cities-and-inclusions.md` is the contract.
   */
  | 'cities'
  /*
   * Campaign inclusions are an eighth master and the only one that carries a
   * PAIR of fields nothing may derive from each other: `unitSingular` and
   * `unitPlural`. `docs/api/cities-and-inclusions.md` is the contract.
   */
  | 'campaignInclusions'
  /*
   * The pipeline picker, and the only master here carrying pipeline metadata:
   * colour, probability, and the two terminal flags. `docs/api/deals.md` is
   * the contract. The pipeline is a table (`PATCH /deal-stages/{id}`), not an
   * enum, precisely so this form can edit it.
   */
  | 'dealStages'
  /*
   * More masters sharing the same parameterised module as the four above
   * (`shape: 'reference'`) — `reference.registry.ts` mounts one router per
   * entry and the schema is identical across every one of them; see `KINDS`
   * below. ⚠️ *SUPERSEDED: "mounts **seven** entries, not four."* **Count
   * `REFERENCE_KINDS`, never a number in a sentence** — this comment has
   * already been wrong about the figure once.
   *
   * `companyStages` matters most: Lead / Prospect / Client / Past Client is a
   * table rather than hardcoded values so a fifth stage can be added as a row,
   * not a migration.
   *
   * `companySources` — where an account CAME FROM — is the same argument for a
   * second column on the same record, and it is NOT a variant of the stage: an
   * account's origin does not change as the account progresses. The two are
   * edited side by side on `/catalog`, so their nouns must stay distinguishable
   * in a refusal ("company stage" / "company source").
   *
   * `companyTypes` — Institution / Partner, seeded — is a THIRD column on that
   * same record and is none of the other two. A stage is where an account IS, a
   * source is where it CAME FROM, and a type is what the organisation ACTUALLY
   * IS. All three are edited within a screen of each other, so "company type"
   * has to stay a distinguishable noun in a refusal alongside the other two.
   */
  | 'companyStages'
  | 'companySources'
  | 'companyTypes'
  | 'quoteStages'
  | 'invoiceStatuses'
  /* Why a deal was lost (migration 285); asked when a deal moves to an `isLost` stage. */
  | 'dealLostReasons'
  /* Suggestions for a contact's free-text `title` (owner 2026-10-18). */
  | 'contactTitles'
  /* Project price tiers (migration 326); exactly one carries `isDefault`. */
  | 'priceTiers'
  /*
   * The two deal pickers — NOT the `reference` family above despite the
   * resemblance: `active` (not `isActive`) as the list filter, `perPage` 100
   * (not 200), ordering `sortOrder, id` (not `sortOrder, name`), `name` to 120
   * (not 60), and a signed `sortOrder` where a negative is legal. Hence their
   * own `picker` shape.
   *
   * `leadSources` is SHARED, not deal-specific — `/lead-sources` is mounted
   * top-level. Nothing on this form may describe it as belonging to deals.
   */
  | 'dealTypes'
  | 'leadSources';

/**
 * The row being edited, or `null` to create.
 *
 * Only `id` is required, so a caller holding nothing but an id still compiles;
 * everything else is optional and, when present, seeds the form. There is no
 * get-by-id method on any of these endpoints, so the form cannot re-fetch the
 * record — it is seeded from the row the list already loaded.
 */
export interface MasterRecordSeed {
  id: number;
  // The four picklists.
  name?: string;
  sortOrder?: number;
  // Currencies.
  currencyName?: string;
  currencyCode?: string;
  // Countries.
  countryName?: string;
  countryCode?: string | null;
  countryFlagCode?: string | null;
  countryCallingCode?: string | null;
  popularStudyDestination?: boolean;
  continentId?: number | null;
  regionId?: number | null;
  currencyId?: number | null;
  /*
   * Cities. These are the RESOLVED objects a city reads back — `{id, name}` —
   * and not the `countryId` / `regionId` above, which are a country's own
   * upstream scalars. The read key and the write key differ on a city, so the
   * seed carries what the API returns and the payload builders convert.
   *
   * ⚠️ `region` here is SEED's Region of focus. It is NOT `regionId` two lines
   * up, which is upstream geography on an unrelated id sequence. They share a
   * word and nothing else, and joining them returns India correctly and Egypt
   * as "South East Asia" — plausible enough to ship.
   */
  country?: MasterRef | null;
  region?: MasterRef | null;
  /*
   * Campaign inclusions. Two independent nouns, both genuinely nullable, and
   * NEITHER is derived from the other — see the note on `inclusionCreate`.
   * Both null is a real configuration ("Priority Support"), not a blank to be
   * filled in.
   */
  unitSingular?: string | null;
  unitPlural?: string | null;
  /*
   * Campaign inclusions. Not a label — see the control's own note. Optional
   * here like every other seed key, and `false` is the right reading of an
   * absent one: it is the column's default and the safe direction.
   */
  trackedAsDeliverable?: boolean;
  /*
   * Campaign inclusions. The catalogue product this inclusion IS when it is
   * delivered — the RESOLVED object, `{id, productName}`, against the written
   * `deliveredAsProductId`. Read key ≠ write key, as on a city.
   *
   * ⚠️ `productName`, NOT `name`. Every other resolved object seeded here is a
   * `MasterRef` (`{id, name}`); the catalog module spells its own column
   * `product_name`, so a renderer written against `MasterRef` compiles here and
   * prints `undefined`. Typed structurally rather than as `ProductSummary`
   * because the tracker read carries the two keys and not `productCode`.
   *
   * `null` and absent are the same fact and the ORDINARY one: four of the five
   * live inclusions have no mapping.
   */
  deliveredAsProduct?: { id: number; productName: string } | null;
  /*
   * Deal stages. `colour` is `"#rrggbb"` or null — null is "nobody has chosen
   * one", a real state, and NOT a reason to paint a grey square. `isWon` and
   * `isLost` are INDEPENDENT flags rather than a state machine, so both are
   * read and neither is inferred from the other.
   */
  colour?: string | null;
  probability?: number;
  isWon?: boolean;
  isLost?: boolean;
  /* The stage a deal moves to when a quote is raised or linked (2026-10-18). */
  isQuoteSent?: boolean;
  /* The stage a deal moves to when a linked quote is signed (2026-10-27). */
  isQuoteSigned?: boolean;
  /*
   * Company types only (migration 325, 2026-10-27): the type counts as Partner
   * in project capacity. `undefined` = an older API, seeded as unticked and
   * never sent unless changed.
   */
  isPartner?: boolean;
  /* Price tiers only (migration 326): the default tier. Same undefined rule. */
  isDefault?: boolean;
  // Every kind on this form, without exception.
  isActive?: boolean;
}

/**
 * Every row shape this form can save, as one union.
 *
 * Handed to `onSaved` so a caller that opened this form to fill a gap in its own
 * picker can use the result, rather than re-reading the master and guessing
 * which row is the new one.
 *
 * **Narrow it with `in`, never with a cast.** The members are distinguishable
 * without a discriminant field: `country` and `region` TOGETHER appear on `City`
 * alone — a `Country` carries `regionId`, an upstream geography scalar, and no
 * `country` of its own — so `'country' in saved && 'region' in saved` narrows to
 * `City` and stops compiling the day a ninth master arrives carrying both. A
 * cast would keep compiling and start lying.
 *
 * ⚠️ Exported as a TYPE only, deliberately: a runtime guard exported from this
 * file would break fast refresh for the component beside it. Do the narrowing at
 * the call site, which is where the `kind` that justifies it is also written.
 */
export type MasterSavedRecord =
  | ReferenceRecord
  | Currency
  | Country
  | City
  | CampaignInclusion
  | DealStage
  | DealType
  | LeadSource;

export interface MasterFormModalProps {
  open: boolean;
  kind: MasterKind;
  /** null = create. */
  record: MasterRecordSeed | null;
  onClose: () => void;
  /**
   * Fired after a successful save, with the row the server returned.
   *
   * The argument is additive: every existing caller passes a `() => void`
   * reload, which stays valid and ignores it. `null` never arrives today — the
   * parameter is nullable so a future branch that saves nothing has somewhere
   * to say so rather than inventing a record.
   */
  onSaved: (saved: MasterSavedRecord | null) => void;
}

/* -------------------------------------------------------------------------
 * Per-kind configuration
 * --------------------------------------------------------------------- */

type Shape =
  | 'reference'
  | 'currency'
  | 'country'
  | 'city'
  | 'inclusion'
  | 'dealStage'
  /**
   * Deal types and lead sources: the same three boxes as `reference`, and a
   * different contract behind every one of them. Kept separate rather than
   * folded in, because the two families are documented as diverging and the
   * deal pickers are the half that will move — `docs/api/invoices.md` gotcha 10
   * lists the differences and says in so many words to write new code against
   * the reference-master shape. A shared shape would quietly apply a
   * `SMALLINT UNSIGNED` ceiling and a name-tie-break to a table that has
   * neither.
   */
  | 'picker';

interface KindConfig {
  shape: Shape;
  /** Singular, lower case — used mid-sentence in every string on this form. */
  noun: string;
  nameLabel: string;
  /** The server's own maximum, or null where the contract doesn't state one. */
  nameMax: number | null;
  /**
   * The server's ceiling on `sortOrder`, where the column is wider than the
   * picklists'. Those four are SMALLINT UNSIGNED — 65,535 — while cities and
   * campaign inclusions store a plain `INT`. Reusing the smaller number would
   * reject values the API accepts, which is the one thing client-side
   * validation must never do. Omit to take `SORT_ORDER_MAX`.
   */
  sortOrderMax?: number;
  /**
   * Deal stages alone: `d_deal_stages.sort_order` is a SIGNED `INT` and the
   * contract says in so many words that any integer, "including negatives", is
   * accepted. Every other master here is validated with a digits-only pattern,
   * which would refuse a value this endpoint takes — the one thing client-side
   * validation must never do — so the floor is configuration rather than a
   * constant. Omit for a floor of 0.
   */
  sortOrderMin?: number;
}

/**
 * Cities store `sort_order` as a signed `INT`, not the picklists' narrower
 * `SMALLINT UNSIGNED`, so the ceiling is different. Mirroring the server rather
 * than reusing the smaller number: a limit invented here would refuse a value
 * the API accepts. Declared above `KINDS` because `KINDS` reads it at module
 * load, where a `const` further down is still in its temporal dead zone.
 */
const CITY_SORT_ORDER_MAX = 2147483647;

const KINDS: Record<MasterKind, KindConfig> = {
  regions: { shape: 'reference', noun: 'region of focus', nameLabel: 'Name', nameMax: 60 },
  bookingStatuses: { shape: 'reference', noun: 'booking status', nameLabel: 'Name', nameMax: 60 },
  travelPackages: { shape: 'reference', noun: 'travel package', nameLabel: 'Name', nameMax: 60 },
  adsPlans: { shape: 'reference', noun: 'ads plan', nameLabel: 'Name', nameMax: 60 },
  currencies: {
    shape: 'currency',
    noun: 'currency',
    nameLabel: 'Currency name',
    // The contract documents the code's width but not the name's, so nothing is
    // enforced here — inventing a limit would reject values the API accepts.
    nameMax: null,
  },
  countries: { shape: 'country', noun: 'country', nameLabel: 'Country name', nameMax: 80 },
  // 1–120, trimmed, and unique per COUNTRY rather than globally.
  cities: {
    shape: 'city',
    noun: 'city',
    nameLabel: 'City name',
    nameMax: 120,
    sortOrderMax: CITY_SORT_ORDER_MAX,
  },
  /*
   * 1–120, trimmed, and unique GLOBALLY — unlike a city, whose unique key is
   * `(name, country_id)`. `sort_order` is the same plain `INT` as a city's.
   */
  campaignInclusions: {
    shape: 'inclusion',
    noun: 'campaign inclusion',
    nameLabel: 'Name',
    nameMax: 120,
    sortOrderMax: CITY_SORT_ORDER_MAX,
  },
  /*
   * `name` is 1–120, trimmed and UNIQUE across the table. `sort_order` is the
   * same signed `INT` as a city's — but unlike a city, negatives are documented
   * as accepted here, hence `sortOrderMin`.
   */
  dealStages: {
    shape: 'dealStage',
    noun: 'deal stage',
    nameLabel: 'Stage name',
    nameMax: 120,
    sortOrderMax: CITY_SORT_ORDER_MAX,
    sortOrderMin: -CITY_SORT_ORDER_MAX - 1,
  },
  /*
   * The remaining reference masters. `nameMax: 60` and the default `sortOrder`
   * range are not a house style — they are `reference.schema.ts`'s
   * `VARCHAR(60)` and `SMALLINT UNSIGNED`, shared with the four picklists above
   * because it is literally the same schema.
   */
  companyStages: { shape: 'reference', noun: 'company stage', nameLabel: 'Name', nameMax: 60 },
  /*
   * ⚠️ The noun is "company source", NOT "source" — it sits one row from
   * "company stage" on `/catalog`, and a refusal reading "That source name is
   * already taken" would not say which of the two masters refused it. It is
   * also a word `/contacts` already uses for a DIFFERENT master (a contact's
   * lead source), so the qualifier is what keeps the two apart on screen.
   */
  companySources: { shape: 'reference', noun: 'company source', nameLabel: 'Name', nameMax: 60 },
  /*
   * ⚠️ The noun is "company type", NOT "type" — a bare "type" is the single
   * most overloaded word on this form, which already edits deal types two rows
   * down and describes every master's own `shape` as a type in its comments. A
   * refusal reading "That type name is already taken" would name none of them.
   */
  companyTypes: { shape: 'reference', noun: 'company type', nameLabel: 'Name', nameMax: 60 },
  quoteStages: { shape: 'reference', noun: 'quote stage', nameLabel: 'Name', nameMax: 60 },
  invoiceStatuses: {
    shape: 'reference',
    noun: 'invoice status',
    nameLabel: 'Name',
    nameMax: 60,
  },
  dealLostReasons: {
    shape: 'reference',
    noun: 'deal lost reason',
    nameLabel: 'Name',
    nameMax: 60,
  },
  contactTitles: {
    shape: 'reference',
    noun: 'contact title',
    nameLabel: 'Name',
    nameMax: 60,
  },
  priceTiers: {
    shape: 'reference',
    noun: 'price tier',
    nameLabel: 'Name',
    nameMax: 60,
  },
  /*
   * The two deal pickers. `name` is 1–120 and UNIQUE; `sort_order` is the same
   * signed `INT` as a deal stage's, negatives included — same floor as
   * `dealStages`, not the picklists' 0.
   */
  dealTypes: {
    shape: 'picker',
    noun: 'deal type',
    nameLabel: 'Name',
    nameMax: 120,
    sortOrderMax: CITY_SORT_ORDER_MAX,
    sortOrderMin: -CITY_SORT_ORDER_MAX - 1,
  },
  leadSources: {
    shape: 'picker',
    noun: 'lead source',
    nameLabel: 'Name',
    nameMax: 120,
    sortOrderMax: CITY_SORT_ORDER_MAX,
    sortOrderMin: -CITY_SORT_ORDER_MAX - 1,
  },
};

/**
 * `#rrggbb`, case-insensitive — the ONLY thing `colour` accepts.
 *
 * `#abc` shorthand, `red` and `rgb(0,0,0)` are each a 422 naming `colour`, so
 * every one of them is caught here instead: a round trip to be told a brand
 * guide's three-digit hex is invalid is a worse way to learn it. The server
 * stores lower-case, and so does this form, so the value typed, previewed and
 * saved is one and the same string.
 */
const HEX_COLOUR = /^#[0-9a-f]{6}$/i;

/**
 * What the colour well shows when the box holds no usable hex.
 *
 * It is NOT written into the form and never sent: `<input type="color">` has no
 * empty state — it falls back to `#000000` — so a well left to its own devices
 * would say the stage is black when the honest answer is that it has no colour.
 * The swatch preview beside it renders nothing at all in that case, which is
 * the state the list and the deal pill also render.
 */
const COLOUR_WELL_FALLBACK = '#cccccc';

/** 0..100, integer, and a REAL JSON number on the wire — "50" is a 422. */
const PROBABILITY_MAX = 100;

/** `unit_singular` and `unit_plural` are both VARCHAR(40). */
const UNIT_MAX = 40;

/**
 * The masters that share one parameterised endpoint; the kind IS the path key,
 * resolved by `REFERENCE_PATHS` in `api.ts`.
 *
 * ⚠️ *SUPERSEDED: "**The SEVEN masters** … Seven, not four."* **This set is the
 * count** — `reference.registry.ts` mounts the same router per entry and a
 * number written in prose beside a list somebody extends goes stale silently.
 * Anything in this set routes through `createReference` / `updateReference` /
 * `deleteReference` and needs no method of its own.
 *
 * ⚠️ **A kind missing from here still TYPECHECKS.** The set is `Set<string>`,
 * not keyed on `MasterKind`, so an omission is not a build error — it is a
 * create that falls through to whichever bespoke branch handles the shape and
 * fails at runtime. Adding a `shape: 'reference'` entry to `KINDS` means adding
 * it here in the same edit.
 */
const REFERENCE_KINDS = new Set<string>([
  'regions',
  'bookingStatuses',
  'travelPackages',
  'adsPlans',
  'invoiceStatuses',
  'quoteStages',
  'companyStages',
  'companySources',
  'companyTypes',
  'dealLostReasons',
  'contactTitles',
  'priceTiers',
]);

/* -------------------------------------------------------------------------
 * Form shape
 *
 * One flat state covering all three payload shapes, held entirely as strings
 * (and two real booleans) so the live form and the loaded record can be
 * compared key by key. That comparison is the whole PATCH story: an omitted key
 * is left untouched, so only genuinely changed keys may be sent — and a PATCH
 * with no keys at all is a 422, which is why Save is disabled until something
 * moves.
 * --------------------------------------------------------------------- */

type FormField =
  | 'name'
  | 'code'
  | 'flagCode'
  | 'callingCode'
  | 'sortOrder'
  | 'currencyId'
  | 'countryId'
  | 'regionId'
  | 'unitSingular'
  | 'unitPlural'
  | 'trackedAsDeliverable'
  | 'deliveredAsProductId'
  | 'colour'
  | 'probability'
  | 'isWon'
  | 'isLost'
  | 'isQuoteSent'
  | 'isQuoteSigned'
  | 'isPartner'
  | 'isDefault'
  | 'popular'
  | 'isActive';

type FormState = {
  name: string;
  code: string;
  flagCode: string;
  callingCode: string;
  sortOrder: string;
  currencyId: string;
  /** Cities only. '' means "no country recorded", which is a legal state. */
  countryId: string;
  /** Cities only. '' means "unclassified", which is a legal state. */
  regionId: string;
  /**
   * Campaign inclusions only. The noun for a quantity of exactly 1. '' means
   * "no unit", which is a legal state and renders the inclusion as a bare name.
   */
  unitSingular: string;
  /**
   * Campaign inclusions only. The noun for every other quantity. **Never
   * derived from `unitSingular`** — see `inclusionCreate`.
   */
  unitPlural: string;
  /**
   * Campaign inclusions only. A real boolean like `isActive`, not a string:
   * the column is NOT NULL, so there is no empty spelling to hold. What it
   * actually does is written at the control that sets it.
   */
  trackedAsDeliverable: boolean;
  /**
   * Campaign inclusions only. The catalogue product id as a string, like every
   * other picker here. `''` means "not a catalogue product", which is a legal
   * state and the one four of the five live inclusions are in — it is sent as
   * an explicit `null`, never as `''`.
   */
  deliveredAsProductId: string;
  /**
   * Deal stages only. Held as the typed TEXT, not as a parsed colour: `''`
   * means "no swatch", which is a real state the API stores as `null`, and a
   * hex pasted from a brand guide has to survive being half-typed on its way in.
   */
  colour: string;
  /** Deal stages only. A string like every other box, parsed once at send. */
  probability: string;
  /** Deal stages only. Independent flags — neither is derived from the other. */
  isWon: boolean;
  isLost: boolean;
  /** Deal stages only. Where a quote moves the deal; exclusive of won/lost in spirit, warned not refused. */
  isQuoteSent: boolean;
  /** Deal stages only. Where a signed quote moves the deal; the server refuses it with won, lost or quote sent (422). */
  isQuoteSigned: boolean;
  /** Company types only. Partner category for project capacity (migration 325). */
  isPartner: boolean;
  /** Price tiers only. The default tier (migration 326); the server keeps exactly one. */
  isDefault: boolean;
  popular: boolean;
  isActive: boolean;
};

type FieldErrors = Partial<Record<FormField, string>>;

/**
 * Server field paths mapped onto form fields, per shape.
 *
 * The API's 422 `details[].path` matches the request key exactly, and the
 * request key is never the form field name here (`currencyName` and
 * `countryName` are both rendered by the field called `name`). Without this map
 * a validation message would land nowhere.
 *
 * `continentId` and `regionId` are absent on purpose: this form never sends
 * them, so the server can never complain about them.
 */
const PATHS: Record<Shape, Record<string, FormField>> = {
  reference: {
    name: 'name',
    sortOrder: 'sortOrder',
    isPartner: 'isPartner',
    isDefault: 'isDefault',
    isActive: 'isActive',
  },
  currency: { currencyName: 'name', currencyCode: 'code', isActive: 'isActive' },
  country: {
    countryName: 'name',
    countryCode: 'code',
    countryFlagCode: 'flagCode',
    countryCallingCode: 'callingCode',
    popularStudyDestination: 'popular',
    currencyId: 'currencyId',
    isActive: 'isActive',
  },
  /*
   * A city's write keys ARE its 422 paths, and they are not its read keys:
   * `countryId` / `regionId` go up, `country` / `region` come back. Both
   * write-time existence checks report against these names —
   * `Country 999 does not exist` on `countryId`, `Region 9 does not exist or is
   * not active` on `regionId` — so both land beside the picker that caused them.
   */
  city: {
    name: 'name',
    countryId: 'countryId',
    regionId: 'regionId',
    sortOrder: 'sortOrder',
    isActive: 'isActive',
  },
  /*
   * Write and read keys are the same strings, so this map is an identity
   * — kept anyway: an absent shape here would silently send every message to
   * the form-level banner instead of the field. (No count is printed here: the
   * key list is a thing somebody adds to, and it has been added to once.)
   */
  inclusion: {
    name: 'name',
    unitSingular: 'unitSingular',
    unitPlural: 'unitPlural',
    trackedAsDeliverable: 'trackedAsDeliverable',
    /*
     * The write key, which is also the 422 path — the read key is
     * `deliveredAsProduct` and never appears in a request, so it is never a
     * path either. A write-time existence check ("Product 9 does not exist")
     * lands on the picker that caused it, as `countryId` does on a city.
     */
    deliveredAsProductId: 'deliveredAsProductId',
    sortOrder: 'sortOrder',
    isActive: 'isActive',
  },
  /*
   * Another identity map, same reason as the inclusion one above — plus one
   * path worth flagging: the server spells it `colour`. `color` is an unknown
   * key on a `.strict()` schema (a 422 with an EMPTY path), so it would surface
   * in the banner rather than on the colour box. Nothing here sends `color`.
   */
  dealStage: {
    name: 'name',
    colour: 'colour',
    probability: 'probability',
    isWon: 'isWon',
    isLost: 'isLost',
    isQuoteSent: 'isQuoteSent',
    isQuoteSigned: 'isQuoteSigned',
    sortOrder: 'sortOrder',
    isActive: 'isActive',
  },
  // Same three keys as `reference`, written out rather than aliased to it: the
  // two contracts are documented as diverging, and sharing the object would tie
  // them together so a key added to one silently applies to the other.
  picker: { name: 'name', sortOrder: 'sortOrder', isActive: 'isActive' },
};

/**
 * The UNIQUE column on each shape — the field a 409 belongs to.
 *
 * A duplicate is a 409, not a 422, so it arrives with no `details` array and
 * would otherwise render as a nameless form-level error. Each shape has exactly
 * one unique column, so the mapping is unambiguous.
 */
const CONFLICT_FIELD: Record<Shape, FormField> = {
  reference: 'name',
  currency: 'code',
  country: 'code',
  /*
   * A city's unique key is `(name, country_id)`, not `name` alone — two
   * Cambridges in two countries are legal, and so are two country-less
   * Cambridges, because MySQL treats NULLs in a unique key as distinct. The
   * 409 belongs on the name box because that is what the user would change,
   * and the server's own message names the country for exactly this reason.
   */
  city: 'name',
  // `name` is unique GLOBALLY, e.g. `A campaign inclusion named "Campus Outreach" already exists`.
  inclusion: 'name',
  // e.g. `A deal stage named "Quote Shared" already exists`.
  dealStage: 'name',
  // e.g. `A deal type named "Renewal" already exists` / a lead source likewise.
  picker: 'name',
};

const SORT_ORDER_MAX = 65535;

function blankForm(): FormState {
  return {
    name: '',
    code: '',
    flagCode: '',
    callingCode: '',
    // Not auto-assigned by the server and defaulted to 0 — so the form shows the
    // value that will actually be stored rather than an empty box.
    sortOrder: '0',
    currencyId: '',
    countryId: '',
    regionId: '',
    /*
     * Both blank, and neither is prefilled from the other at any point. An
     * inclusion with no units is a real configuration, so an empty pair is a
     * valid form rather than an unfinished one.
     */
    unitSingular: '',
    unitPlural: '',
    /*
     * Off on a new inclusion, matching the column's own default. The safe
     * direction: an inclusion that should have produced tracker rows and does
     * not is a question somebody asks, where rows nobody expected are rows
     * somebody plans delivery against.
     */
    trackedAsDeliverable: false,
    /*
     * Empty, and NOT guessed from the name. `Meet-Up` and `Meet Up` do not
     * match on any comparison worth writing — see `productOptions` — so a new
     * inclusion starts unmapped, which is the ordinary state anyway.
     */
    deliveredAsProductId: '',
    /*
     * Empty, and NOT seeded with a suggested hex. `null` is what the column
     * defaults to and "no swatch" is a supported state — prefilling one would
     * mean every new stage silently acquired a colour nobody chose.
     */
    colour: '',
    // The server's own default, shown rather than left blank so the form says
    // what will actually be stored.
    probability: '0',
    isWon: false,
    isLost: false,
    isQuoteSent: false,
    isQuoteSigned: false,
    isPartner: false,
    isDefault: false,
    popular: false,
    isActive: true,
  };
}

/** The loaded row as form values — the baseline every PATCH diff is taken against. */
function recordForm(kind: MasterKind, record: MasterRecordSeed | null): FormState {
  const blank = blankForm();
  if (!record) return blank;

  switch (KINDS[kind].shape) {
    case 'reference':
      return {
        ...blank,
        name: record.name ?? '',
        sortOrder: String(record.sortOrder ?? 0),
        isPartner: record.isPartner ?? false,
        isDefault: record.isDefault ?? false,
        isActive: record.isActive ?? true,
      };
    case 'currency':
      return {
        ...blank,
        name: record.currencyName ?? '',
        code: record.currencyCode ?? '',
        isActive: record.isActive ?? true,
      };
    case 'country':
      return {
        ...blank,
        name: record.countryName ?? '',
        code: record.countryCode ?? '',
        flagCode: record.countryFlagCode ?? '',
        callingCode: record.countryCallingCode ?? '',
        currencyId: record.currencyId === null || record.currencyId === undefined
          ? ''
          : String(record.currencyId),
        popular: record.popularStudyDestination ?? false,
        isActive: record.isActive ?? true,
      };
    case 'city':
      // Seeded from the resolved `{id, name}` objects a city reads back; null
      // country/region are real states ("no country recorded" / "unclassified").
      return {
        ...blank,
        name: record.name ?? '',
        countryId: record.country ? String(record.country.id) : '',
        regionId: record.region ? String(record.region.id) : '',
        sortOrder: String(record.sortOrder ?? 0),
        isActive: record.isActive ?? true,
      };
    case 'inclusion':
      // null on either unit is a real state (bare-name render), not missing data.
      return {
        ...blank,
        name: record.name ?? '',
        unitSingular: record.unitSingular ?? '',
        unitPlural: record.unitPlural ?? '',
        trackedAsDeliverable: record.trackedAsDeliverable ?? false,
        // Seeded from the resolved `{id, productName}`; null/absent is "not a
        // catalogue product", a real state rather than missing data.
        deliveredAsProductId: record.deliveredAsProduct
          ? String(record.deliveredAsProduct.id)
          : '',
        sortOrder: String(record.sortOrder ?? 0),
        isActive: record.isActive ?? true,
      };
    case 'dealStage':
      // `colour: null` and unset flags seed as real states — see `dealStagePatch`
      // for why the empty-box diff matters, and `MasterRecordSeed` for the flags.
      return {
        ...blank,
        name: record.name ?? '',
        colour: record.colour ?? '',
        probability: String(record.probability ?? 0),
        isWon: record.isWon ?? false,
        isLost: record.isLost ?? false,
        isQuoteSent: record.isQuoteSent ?? false,
        isQuoteSigned: record.isQuoteSigned ?? false,
        sortOrder: String(record.sortOrder ?? 0),
        isActive: record.isActive ?? true,
      };
    case 'picker':
      // Same three values as a reference master; createdBy/modifiedBy are never
      // seeded, offered or sent — see `pickerCreate`.
      return {
        ...blank,
        name: record.name ?? '',
        sortOrder: String(record.sortOrder ?? 0),
        isActive: record.isActive ?? true,
      };
  }
}

/* ---- payloads -------------------------------------------------------- */

/**
 * The company-type flag (migration 325). Kept local rather than on the shared
 * `ReferenceCreateInput`: every other master refuses the key (422), so it is
 * only ever added by the two builders below, and only for `companyTypes`.
 */
type CompanyTypeFlags = { isPartner?: boolean; isDefault?: boolean };

function referenceCreate(kind: MasterKind, form: FormState): ReferenceCreateInput & CompanyTypeFlags {
  // All three keys are sent: the schema is `.strict()` but these are its own
  // three keys, and sending them makes what was saved match what was on screen.
  return {
    name: form.name.trim(),
    sortOrder: Number(form.sortOrder),
    isActive: form.isActive,
    // Sent only when ticked: the server defaults it to false, and an unticked
    // box never has to depend on the key being known.
    ...(kind === 'companyTypes' && form.isPartner ? { isPartner: true } : {}),
    // Price tiers: same rule. The server moves the default onto this row.
    ...(kind === 'priceTiers' && form.isDefault ? { isDefault: true } : {}),
  };
}

function referencePatch(
  kind: MasterKind,
  form: FormState,
  base: FormState,
): ReferenceUpdateInput & CompanyTypeFlags {
  const body: ReferenceUpdateInput & CompanyTypeFlags = {};
  const name = form.name.trim();
  if (name !== base.name.trim()) body.name = name;
  if (Number(form.sortOrder) !== Number(base.sortOrder)) body.sortOrder = Number(form.sortOrder);
  if (kind === 'companyTypes' && form.isPartner !== base.isPartner) body.isPartner = form.isPartner;
  if (kind === 'priceTiers' && form.isDefault !== base.isDefault) body.isDefault = form.isDefault;
  if (form.isActive !== base.isActive) body.isActive = form.isActive;
  return body;
}

/**
 * Deal types and lead sources.
 *
 * Structurally the same three keys `referenceCreate` builds, and deliberately
 * NOT that function: the two contracts diverge on the sort-order range, and
 * `DealTypeCreateInput` is the type that says so. `LeadSourceCreateInput` is
 * identical field for field, so one builder serves both — the shared shape is
 * the API's, not a convenience invented here.
 *
 * No actor key. `createdById` / `modifiedById` were accepted while these
 * routers ran unauthenticated; they were removed when auth landed, both schemas
 * are `.strict()`, and sending either is now a 422 naming the key.
 */
function pickerCreate(form: FormState): DealTypeCreateInput {
  return {
    name: form.name.trim(),
    sortOrder: Number(form.sortOrder),
    isActive: form.isActive,
  };
}

function pickerPatch(form: FormState, base: FormState): DealTypeUpdateInput {
  const body: DealTypeUpdateInput = {};
  const name = form.name.trim();
  if (name !== base.name.trim()) body.name = name;
  if (Number(form.sortOrder) !== Number(base.sortOrder)) body.sortOrder = Number(form.sortOrder);
  if (form.isActive !== base.isActive) body.isActive = form.isActive;
  return body;
}

function currencyCreate(form: FormState): CurrencyCreateInput {
  return {
    currencyName: form.name.trim(),
    // Uppercased client-side too, so the value that is compared, displayed and
    // stored is one and the same string.
    currencyCode: form.code.trim().toUpperCase(),
    isActive: form.isActive,
  };
}

function currencyPatch(form: FormState, base: FormState): CurrencyUpdateInput {
  const body: CurrencyUpdateInput = {};
  const name = form.name.trim();
  if (name !== base.name.trim()) body.currencyName = name;
  const code = form.code.trim().toUpperCase();
  if (code !== base.code.trim().toUpperCase()) body.currencyCode = code;
  if (form.isActive !== base.isActive) body.isActive = form.isActive;
  return body;
}

function countryCreate(form: FormState): CountryCreateInput {
  const body: CountryCreateInput = {
    countryName: form.name.trim(),
    countryCode: form.code.trim().toUpperCase(),
    countryFlagCode: form.flagCode.trim().toUpperCase(),
    // The leading '+' is part of the value, not decoration: "91" is a 422.
    countryCallingCode: form.callingCode.trim(),
    popularStudyDestination: form.popular,
    isActive: form.isActive,
  };
  // Omitted rather than sent as null when unset — on create there is nothing to
  // clear, and `continentId`/`regionId` are never sent at all because this form
  // cannot offer a trustworthy way to choose them.
  if (form.currencyId) body.currencyId = Number(form.currencyId);
  return body;
}

function countryPatch(form: FormState, base: FormState): CountryUpdateInput {
  const body: CountryUpdateInput = {};
  const name = form.name.trim();
  if (name !== base.name.trim()) body.countryName = name;

  const code = form.code.trim().toUpperCase();
  if (code !== base.code.trim().toUpperCase()) body.countryCode = code;

  const flag = form.flagCode.trim().toUpperCase();
  if (flag !== base.flagCode.trim().toUpperCase()) body.countryFlagCode = flag;

  const calling = form.callingCode.trim();
  if (calling !== base.callingCode.trim()) body.countryCallingCode = calling;

  if (form.popular !== base.popular) body.popularStudyDestination = form.popular;

  /*
   * The one place in these six masters where `null` is legal, and it means
   * "clear the column" — every other field would answer a null with a 422.
   */
  if (form.currencyId !== base.currencyId) {
    body.currencyId = form.currencyId ? Number(form.currencyId) : null;
  }

  if (form.isActive !== base.isActive) body.isActive = form.isActive;
  return body;
}

function cityCreate(form: FormState): CityCreateInput {
  const body: CityCreateInput = {
    name: form.name.trim(),
    sortOrder: Number(form.sortOrder),
    isActive: form.isActive,
  };
  // Omitted (not null) when unset: nothing to clear on create, and omitting
  // also skips the write-time existence check that a value — even null — runs.
  if (form.countryId) body.countryId = Number(form.countryId);
  if (form.regionId) body.regionId = Number(form.regionId);
  return body;
}

/**
 * THE DEACTIVATION TRAP IS WHY THIS IS A DIFF AND NOT A SPREAD.
 *
 * `regionId` is checked against the ACTIVE regions on every write, including a
 * write that did not change it. So re-sending the region a city already holds
 * is a 422 — `Region 9 does not exist or is not active` — the moment that
 * region is retired, on a field the user never touched. Sending the key only
 * when the value actually moved is the half of the fix that lives here;
 * `withStoredOption` in the picker is the other half, and one without the other
 * silently loses data.
 */
function cityPatch(form: FormState, base: FormState): CityUpdateInput {
  const body: CityUpdateInput = {};
  const name = form.name.trim();
  if (name !== base.name.trim()) body.name = name;

  /*
   * `null` clears the column here, and both of these are genuinely nullable —
   * unlike `name`, `isActive` and `sortOrder`, where a null is a 422.
   */
  if (form.countryId !== base.countryId) {
    body.countryId = form.countryId ? Number(form.countryId) : null;
  }
  if (form.regionId !== base.regionId) {
    body.regionId = form.regionId ? Number(form.regionId) : null;
  }

  if (Number(form.sortOrder) !== Number(base.sortOrder)) body.sortOrder = Number(form.sortOrder);
  if (form.isActive !== base.isActive) body.isActive = form.isActive;
  return body;
}

/*
 * ✅ **PLAIN ALIASES NOW — the local `deliveredAsProductId` declaration is GONE,
 * 2026-09-12.** *SUPERSEDED: both of these were an INTERSECTION adding
 * `deliveredAsProductId?: number | null`, because this control and backend
 * migration 177 landed in the same hour and `types.ts` did not carry the key
 * yet.*
 *
 * It does now, on `CampaignInclusionCreateInput` — and
 * `CampaignInclusionUpdateInput` is `Partial<…>` of it, so both shapes have it
 * from one declaration. The scaffolding was removed the moment that landed,
 * which is what its own note asked for: **two declarations of one wire key
 * agree on the day they are written and drift on the first correction**, and
 * the local copy is the one nothing else in the app reads — so it drifts
 * silently and this file alone believes it.
 *
 * ⚠️ **The reason it was an intersection and never a cast is worth keeping**,
 * because the next person meeting this pattern will reach for the cast: an
 * intersection ADDS a key while still type-checking the VALUE, so a `''` or an
 * `undefined` reaching the payload stayed a build error throughout. A cast
 * would have silenced the value check too — it keeps compiling and starts
 * lying, which is what `MasterSavedRecord`'s own note in this file says.
 */
type InclusionCreateBody = CampaignInclusionCreateInput;
type InclusionPatchBody = CampaignInclusionUpdateInput;

/**
 * ⚠️ **NOTHING HERE DERIVES ONE UNIT FORM FROM THE OTHER, AND NOTHING MAY.**
 *
 * `unitSingular` and `unitPlural` are two columns precisely because `campus` →
 * `campuses` follows no rule worth writing — and this text prints on a quote or
 * an invoice a customer receives. Appending an "s" would be wrong for the very
 * first seeded row. So the form collects both independently, sends exactly what
 * was typed, and offers no silent fill.
 *
 * An empty box is sent as `null`, not as `""`: the server normalises `""` to
 * `null` anyway, so sending the empty string would mean the value stored and
 * the value sent disagree. Both null is a supported configuration — the
 * inclusion then renders as a bare name with no number.
 */
function inclusionCreate(form: FormState): InclusionCreateBody {
  const body: InclusionCreateBody = {
    name: form.name.trim(),
    unitSingular: form.unitSingular.trim() || null,
    unitPlural: form.unitPlural.trim() || null,
    // Sent on create like every other key here, so what was saved matches what
    // was on screen. There is no `null` spelling: the column is NOT NULL, and
    // an explicit `null` is a 422.
    trackedAsDeliverable: form.trackedAsDeliverable,
    sortOrder: Number(form.sortOrder),
    isActive: form.isActive,
  };
  /*
   * OMITTED when unset, not sent as null — the same rule as `countryId` on a
   * city, and NOT the rule the two units above follow. There is nothing to
   * clear on a row that does not exist yet, and omitting also skips the
   * write-time existence check that any value, `null` included, would run.
   */
  if (form.deliveredAsProductId) {
    body.deliveredAsProductId = Number(form.deliveredAsProductId);
  }
  return body;
}

function inclusionPatch(form: FormState, base: FormState): InclusionPatchBody {
  const body: InclusionPatchBody = {};

  const name = form.name.trim();
  if (name !== base.name.trim()) body.name = name;

  /*
   * The two units are diffed SEPARATELY and sent separately. Changing one must
   * never rewrite the other, and `null` here is a real instruction — it clears
   * the column — rather than the 422 a null on `name`, `sortOrder` or
   * `isActive` would be.
   */
  const singular = form.unitSingular.trim();
  if (singular !== base.unitSingular.trim()) body.unitSingular = singular || null;

  const plural = form.unitPlural.trim();
  if (plural !== base.unitPlural.trim()) body.unitPlural = plural || null;

  /*
   * Diffed like every other key, and the diff is what keeps it off an
   * unrelated save: re-sending an unchanged `true` would be a no-op today and
   * a tracker-wide rewrite the day the server does anything on transition.
   */
  if (form.trackedAsDeliverable !== base.trackedAsDeliverable) {
    body.trackedAsDeliverable = form.trackedAsDeliverable;
  }

  /*
   * `null` CLEARS and `''` is not a spelling this key has — sending the empty
   * string would be a 422 on a `.strict()` number-or-null schema, and the two
   * are one keystroke apart in the form state, which is why the conversion
   * happens here rather than at the control.
   *
   * ⚠️ Diffed, like every other key, and that is what keeps the mapping off an
   * unrelated save. A spread would re-send the stored id on every rename — and
   * the day this key gains a write-time active check, re-asserting a mapping
   * to a since-retired product would 422 on a field nobody touched. That is
   * `cityPatch`'s deactivation trap, and the diff is half the fix;
   * `productOptions` injecting the stored row is the other half.
   *
   * ⚠️ It is NOT cleared when the tickbox above it is unticked. Untracking an
   * inclusion stops it producing tracker rows; it does not stop it BEING that
   * product, and a silent `null` on a key the user never touched is the
   * "removing a control does not remove a write" failure in a fresh spelling.
   */
  if (form.deliveredAsProductId !== base.deliveredAsProductId) {
    body.deliveredAsProductId = form.deliveredAsProductId
      ? Number(form.deliveredAsProductId)
      : null;
  }

  if (Number(form.sortOrder) !== Number(base.sortOrder)) body.sortOrder = Number(form.sortOrder);
  if (form.isActive !== base.isActive) body.isActive = form.isActive;
  return body;
}

/**
 * The colour as the API wants it, or `null` for "no swatch".
 *
 * Lower-cased here rather than left to the server, so the string this form
 * compares, previews and sends is the string that comes back on the next read.
 * Without that a `#FFD54F` typed in caps would diff unequal against the stored
 * `#ffd54f` for ever and re-send itself on every unrelated save.
 */
function colourValue(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

function dealStageCreate(form: FormState): DealStageCreateInput {
  const body: DealStageCreateInput = {
    name: form.name.trim(),
    // Real JSON numbers: the picker schemas use plain `z.number()`, so "50" is
    // a 422 here even though the deals endpoints would have coerced it.
    probability: Number(form.probability),
    isWon: form.isWon,
    isLost: form.isLost,
    isActive: form.isActive,
    sortOrder: Number(form.sortOrder),
  };
  // Sent only when ticked: the key is newer than the other flags (2026-10-18),
  // and an API that predates it rejects unknown keys on a `.strict()` schema.
  if (form.isQuoteSent) body.isQuoteSent = true;
  // Same reason (2026-10-27).
  if (form.isQuoteSigned) body.isQuoteSigned = true;
  // Omitted, not null, when empty — nothing to clear on create.
  const colour = colourValue(form.colour);
  if (colour !== null) body.colour = colour;
  return body;
}

/**
 * ⚠️ **A RENAME MUST NOT WIPE THE SWATCH, AND THIS DIFF IS WHY IT DOES NOT.**
 *
 * `colour`: omitted keeps the stored swatch, `null` clears it. A payload built
 * by spreading the form would send `colour` on every save — a silent erasure
 * the moment somebody edits a stage whose colour they never looked at.
 *
 * `probability` has the same asymmetry: no default on PATCH, so omitting it
 * leaves the stored percentage alone. It drives expected revenue for every
 * deal at this stage with no override of its own, so a key this form didn't
 * mean to write has no business moving a forecast.
 */
function dealStagePatch(form: FormState, base: FormState): DealStageUpdateInput {
  const body: DealStageUpdateInput = {};

  const name = form.name.trim();
  if (name !== base.name.trim()) body.name = name;

  const colour = colourValue(form.colour);
  if (colour !== colourValue(base.colour)) body.colour = colour;

  if (Number(form.probability) !== Number(base.probability)) {
    body.probability = Number(form.probability);
  }

  // Independent flags, diffed separately. Changing one must never rewrite the
  // other, and `null` on either is a 422 rather than a clear.
  if (form.isWon !== base.isWon) body.isWon = form.isWon;
  if (form.isLost !== base.isLost) body.isLost = form.isLost;
  if (form.isQuoteSent !== base.isQuoteSent) body.isQuoteSent = form.isQuoteSent;
  if (form.isQuoteSigned !== base.isQuoteSigned) body.isQuoteSigned = form.isQuoteSigned;

  if (Number(form.sortOrder) !== Number(base.sortOrder)) body.sortOrder = Number(form.sortOrder);
  if (form.isActive !== base.isActive) body.isActive = form.isActive;
  return body;
}

function buildPatch(kind: MasterKind, form: FormState, base: FormState) {
  switch (KINDS[kind].shape) {
    case 'reference':
      return referencePatch(kind, form, base);
    case 'currency':
      return currencyPatch(form, base);
    case 'country':
      return countryPatch(form, base);
    case 'city':
      return cityPatch(form, base);
    case 'inclusion':
      return inclusionPatch(form, base);
    case 'dealStage':
      return dealStagePatch(form, base);
    case 'picker':
      return pickerPatch(form, base);
  }
}

/* ---- validation ------------------------------------------------------
 *
 * Mirrors the server exactly — no stricter, no looser. Anything checked here is
 * checked so the user gets the message next to the field instead of a round
 * trip; anything the contract leaves open is left to the server.
 * -------------------------------------------------------------------- */

const TWO_LETTERS = /^[A-Za-z]{2}$/;
const THREE_LETTERS = /^[A-Za-z]{3}$/;
const CALLING_CODE = /^\+\d{1,7}$/;
const WHOLE_NUMBER = /^\d+$/;
/**
 * The three DEAL PICKERS — stages, types and lead sources. Their `sort_order`
 * is a signed `INT` and the contract documents negatives as accepted, unlike
 * the reference masters' `SMALLINT UNSIGNED`, where a negative is a 422.
 */
const SIGNED_WHOLE_NUMBER = /^-?\d+$/;

function validate(kind: MasterKind, form: FormState): FieldErrors {
  const config = KINDS[kind];
  const errors: FieldErrors = {};

  const name = form.name.trim();
  if (!name) errors.name = `Enter a name for this ${config.noun}.`;
  else if (config.nameMax !== null && name.length > config.nameMax) {
    errors.name = `Use ${config.nameMax} characters or fewer.`;
  }

  if (config.shape === 'reference') {
    const sortOrder = form.sortOrder.trim();
    if (!sortOrder) {
      // NOT NULL and never nullish server-side: there is no "clear", so an empty
      // box has no valid payload to send.
      errors.sortOrder = 'Enter a sort order — use 0 for none.';
    } else if (!WHOLE_NUMBER.test(sortOrder) || Number(sortOrder) > SORT_ORDER_MAX) {
      errors.sortOrder = `Use a whole number from 0 to ${SORT_ORDER_MAX}.`;
    }
  }

  /*
   * Only the two scalars are checked here. `countryId` and `regionId` are
   * deliberately NOT validated client-side beyond being a choice from the
   * picker: whether a region is still active is the server's question, it is
   * re-asked on every write, and a client that answered it from a list fetched
   * a minute ago would refuse a value the API would take, or accept one it
   * would not.
   */
  if (config.shape === 'city') {
    const max = config.sortOrderMax ?? SORT_ORDER_MAX;
    const sortOrder = form.sortOrder.trim();
    if (!sortOrder) {
      errors.sortOrder = 'Enter a sort order — use 0 for none.';
    } else if (!WHOLE_NUMBER.test(sortOrder) || Number(sortOrder) > max) {
      errors.sortOrder = `Use a whole number from 0 to ${max}.`;
    }
  }

  /*
   * The two units are checked only for LENGTH, and independently of each other.
   *
   * Nothing here requires one when the other is set, and nothing warns that a
   * lone singular is odd — the server accepts it, and a client refusing what
   * the API takes is worse than a configuration somebody meant. The pair being
   * incomplete is instead made VISIBLE by the live preview on the form, which
   * shows exactly what a quote would print at each quantity.
   */
  if (config.shape === 'inclusion') {
    const max = config.sortOrderMax ?? SORT_ORDER_MAX;
    const sortOrder = form.sortOrder.trim();
    if (!sortOrder) {
      errors.sortOrder = 'Enter a sort order — use 0 for none.';
    } else if (!WHOLE_NUMBER.test(sortOrder) || Number(sortOrder) > max) {
      errors.sortOrder = `Use a whole number from 0 to ${max}.`;
    }

    if (form.unitSingular.trim().length > UNIT_MAX) {
      errors.unitSingular = `Use ${UNIT_MAX} characters or fewer.`;
    }
    if (form.unitPlural.trim().length > UNIT_MAX) {
      errors.unitPlural = `Use ${UNIT_MAX} characters or fewer.`;
    }
  }

  /*
   * THE COLOUR IS CHECKED HERE BECAUSE THE SERVER'S ANSWER IS A 422, NOT A
   * TOLERANT PARSE. `colour` accepts `#rrggbb` and nothing else: `#abc`
   * shorthand, `red` and `rgb(30,136,229)` are each refused by name. A hex
   * pasted out of a brand guide is the normal way this field gets filled in and
   * three-digit shorthand is the normal way a brand guide writes one, so the
   * message says what to type rather than relaying "Invalid input".
   *
   * An EMPTY box is valid and means "no swatch" — it is not an unfinished
   * field, and `null` is what the column defaults to.
   */
  if (config.shape === 'dealStage') {
    const colour = form.colour.trim();
    if (colour && !HEX_COLOUR.test(colour)) {
      errors.colour = colour.startsWith('#')
        ? 'Use six hex digits, e.g. #1e88e5.'
        : 'Use a hex code, e.g. #1e88e5.';
    }

    /*
     * 0..100 and an INTEGER. Not coerced server-side, so a decimal typed into a
     * number box would be sent as a real number the schema still refuses; it is
     * caught here instead of coming back as a 422 on a field that looks fine.
     */
    const probability = form.probability.trim();
    if (!probability) {
      // NOT NULL, no clear — same as sortOrder above.
      errors.probability = 'Enter a percentage — use 0 for none.';
    } else if (
      !WHOLE_NUMBER.test(probability) ||
      Number(probability) > PROBABILITY_MAX
    ) {
      errors.probability = `Use a whole number from 0 to ${PROBABILITY_MAX}.`;
    }

    // May be negative — refusing one would be stricter than the API.
    const max = config.sortOrderMax ?? SORT_ORDER_MAX;
    const min = config.sortOrderMin ?? 0;
    const sortOrder = form.sortOrder.trim();
    if (!sortOrder) {
      errors.sortOrder = 'Enter a sort order — use 0 for none.';
    } else if (
      !SIGNED_WHOLE_NUMBER.test(sortOrder) ||
      Number(sortOrder) > max ||
      Number(sortOrder) < min
    ) {
      errors.sortOrder = `Use a whole number from ${min} to ${max}.`;
    }
  }

  // Deal types and lead sources: same signed-INT range as dealStage — negatives
  // legal, so SIGNED_WHOLE_NUMBER, not WHOLE_NUMBER.
  if (config.shape === 'picker') {
    const max = config.sortOrderMax ?? SORT_ORDER_MAX;
    const min = config.sortOrderMin ?? 0;
    const sortOrder = form.sortOrder.trim();
    if (!sortOrder) {
      errors.sortOrder = 'Enter a sort order — use 0 for none.';
    } else if (
      !SIGNED_WHOLE_NUMBER.test(sortOrder) ||
      Number(sortOrder) > max ||
      Number(sortOrder) < min
    ) {
      errors.sortOrder = `Use a whole number from ${min} to ${max}.`;
    }
  }

  if (config.shape === 'currency') {
    const code = form.code.trim();
    if (!code) errors.code = 'Enter the ISO 4217 code.';
    else if (!THREE_LETTERS.test(code)) {
      errors.code = 'Use exactly three letters, e.g. INR.';
    }
  }

  if (config.shape === 'country') {
    const code = form.code.trim();
    if (!code) errors.code = 'Enter the two-letter country code.';
    else if (!TWO_LETTERS.test(code)) errors.code = 'Use exactly two letters, e.g. IN.';

    const flag = form.flagCode.trim();
    if (!flag) errors.flagCode = 'Enter the two-letter flag code.';
    else if (!TWO_LETTERS.test(flag)) errors.flagCode = 'Use exactly two letters, e.g. IN.';

    const calling = form.callingCode.trim();
    if (!calling) errors.callingCode = 'Enter the dialling code, including the +.';
    else if (!CALLING_CODE.test(calling)) {
      errors.callingCode = 'Start with + and up to 7 digits, e.g. +91.';
    }
  }

  return errors;
}

/* ---- currency options ------------------------------------------------ */

/**
 * A country's `currencyId` DOES resolve against `/currencies` — the ids are the
 * upstream ones and have been verified against live data for all 195 countries.
 * This is the exception, not the rule: the adjacent `regionId` must never be
 * resolved the same way. See the comment on the read-only block below.
 *
 * Retired currencies are listed rather than filtered out, and labelled. Nothing
 * on the write path requires the target currency to be active, and hiding the
 * one a country already holds is how a picker silently reassigns a value the
 * user never looked at.
 */
function currencyOptions(rows: Currency[]): ComboboxOption[] {
  return rows.map((row) => ({
    value: String(row.id),
    label: `${row.currencyCode} — ${row.currencyName}`,
    hint: row.isActive ? undefined : 'Retired',
  }));
}

/* ---- country options (cities) ---------------------------------------- */

/**
 * The country picker on a city.
 *
 * **Every country is listed, retired ones included and labelled.** The city
 * write-time check on `countryId` is existence only — it does NOT require the
 * country to be active — and that asymmetry with `regionId` is deliberate and
 * documented. So filtering to the active rows here would hide a value the API
 * accepts, and would drop the country a city already holds out of its own form.
 *
 * The label leads with the name rather than the code, because this list is read
 * by somebody typing "Bangalore", not by somebody reconciling ISO codes.
 */
function countryOptions(rows: Country[]): ComboboxOption[] {
  return rows.map((row) => ({
    value: String(row.id),
    label: row.countryName,
    hint: row.isActive ? (row.countryCode ?? undefined) : 'Retired',
  }));
}

/* ---- product options (campaign inclusions) --------------------------- */

/**
 * The catalogue-product picker on a campaign inclusion.
 *
 * ⚠️ **WHY A PICKER AND NOT A NAME MATCH.** The pair that motivated this column
 * is the inclusion `Meet-Up` and the product `Meet Up` — they differ by a
 * hyphen, so `===` does not match them, and the only comparison that would is a
 * fold over punctuation and case, which is a rule nobody wrote down and every
 * future pair gets to violate. Worse, the name is not stable: inclusion id 4
 * was seeded `Profile Evaluation` by migration 056 and an admin RENAMED it to
 * `Meet-Up` in production, in place. Matching on an admin-editable label is the
 * failure this codebase has already shipped three times (`"Unpaid"` containing
 * `"paid"`, `"Not Accepted"` containing `"accept"`, and a product rename
 * switching inclusions off). A chosen id is a decision somebody made; a name
 * match is a coincidence that holds until somebody edits a label.
 *
 * ⚠️ **`productName`, not `name`** — the catalog module spells its own column
 * `product_name`, unlike every `MasterRef` master on this form. A label built
 * from `row.name` compiles against `Product` nowhere and would print
 * `undefined` if it did.
 *
 * **Every product is listed, retired ones included and labelled**, which is
 * `currencyOptions` and `countryOptions` above rather than the region picker's
 * `isActive: true`. The region picker filters because its write-time check is
 * measured active-only; **this key's write-time check is not measured at all**,
 * it is landing with migration 177 alongside this control. Over-offering costs
 * one visible 422 naming the field; over-restricting hides a value the API may
 * well accept and does it silently. Filter this list only once somebody has
 * watched the server refuse a retired product.
 *
 * `stored` is injected when it is not among the rows — `withStoredOption`'s
 * rule, applied by hand because that helper takes `ReferenceRecord[]` and a
 * product is not one. **Both halves of its reasoning are reproduced, including
 * the one that is easy to drop:** a stored value missing from a NON-EMPTY list
 * really is gone and is labelled so, while a stored value missing from an EMPTY
 * list proves nothing — the rows are still loading, or the request failed — so
 * it is injected with no hint rather than accusing a live product of being
 * retired for the frame or two before the list lands.
 */
function productOptions(
  rows: Product[],
  stored: { id: number; productName: string } | null,
): ComboboxOption[] {
  const options: ComboboxOption[] = rows.map((row) => ({
    value: String(row.id),
    label: row.productName,
    // `productActive`, not `isActive` — the catalog module's own spelling.
    hint: row.productActive ? (row.productCode ?? undefined) : 'Retired',
  }));

  if (!stored) return options;
  if (rows.some((row) => row.id === stored.id)) return options;

  const knownRetired = rows.length > 0;

  return [
    {
      value: String(stored.id),
      label: stored.productName,
      ...(knownRetired ? { hint: 'No longer offered' } : {}),
    },
    ...options,
  ];
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/* ---- the campaign-inclusion preview ---------------------------------- */

/**
 * The contract's rendering rule, applied by the ONE function that owns it.
 *
 * ⚠️ **Never re-type the rule here.** `src/lib/inclusions.ts` is the single
 * copy, read by the line editor, the line table and this admin row; once
 * `LINE_DESCRIPTION_INCLUDES_INCLUSIONS` flips it also composes the text on
 * the customer's Xero document, against a separate implementation in
 * `emerge-backend-app` that nothing compares it with. A local copy here would
 * duplicate the rule on the one screen whose job is showing an admin what the
 * customer will read — a drift would look fine on both sides and error nowhere.
 *
 * What's left here is only form-state → stored-record mapping: an empty box
 * means `null` (same `trim() || null` terms as `inclusionCreate`), so the
 * preview shows what will actually be STORED, not what's momentarily typed.
 */
function previewLine(name: string, unitSingular: string, unitPlural: string, quantity: number) {
  return renderInclusion(
    {
      name: name.trim() || 'This inclusion',
      unitSingular: unitSingular.trim() || null,
      unitPlural: unitPlural.trim() || null,
    },
    quantity,
  );
}

/**
 * A live sample of the two quantities that behave differently.
 *
 * Shown rather than a rule stated in prose, because the failure this master
 * exists to prevent — a singular filled in and a plural left blank, or a plural
 * guessed wrong — is invisible in the boxes and obvious in the output.
 */
function UnitPreview({
  name,
  unitSingular,
  unitPlural,
}: {
  name: string;
  unitSingular: string;
  unitPlural: string;
}) {
  return (
    <div className="rounded-md border border-border bg-surface-sunken px-3 py-2.5">
      <p className="m-0 text-xs font-medium text-fg-secondary">
        On a quote or invoice, this prints as
      </p>
      <ul className="mt-1.5 mb-0 grid list-none gap-1 p-0 text-xs text-fg">
        <li className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-fg-subtle">Quantity 1</span>
          <span className="[overflow-wrap:anywhere]">
            {previewLine(name, unitSingular, unitPlural, 1)}
          </span>
        </li>
        <li className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-fg-subtle">Quantity 5</span>
          <span className="[overflow-wrap:anywhere]">
            {previewLine(name, unitSingular, unitPlural, 5)}
          </span>
        </li>
      </ul>
    </div>
  );
}

/* ---- the deal-stage pipeline notes ------------------------------------ */

/**
 * A tone-carrying note. Amber and a word, never amber alone: everything below
 * is a *consequence* being pointed out, not an error, and Save stays enabled
 * through every one of them.
 */
function StageNote({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Notice tone="warning" role="status">
      <span className="font-medium text-fg">{heading}</span> {children}
    </Notice>
  );
}

/**
 * ⚠️ **THE FORECAST WARNING.** Why the probability box is not just another
 * number field.
 *
 * A deal's expected revenue is `amount × stage.probability` — so editing this
 * box silently moves the expected revenue of EVERY deal at this stage, the
 * moment Save is pressed, with no other screen announcing it.
 *
 * ⚠️ **This got WIDER on 2026-08-31 and the copy had to follow it.**
 * *SUPERSEDED: "`amount × (deal.probability ?? stage.probability)` — the deal's
 * own percentage is an OVERRIDE, so editing this box moves … every deal at this
 * stage WITH NO OVERRIDE OF ITS OWN."* The owner retired the per-deal override,
 * so there is no longer any such thing as a deal exempt from this box: the
 * qualifier that used to make the sentence accurate now makes it understate the
 * blast radius, which is the reassuring direction and the one nobody reports.
 * All three rendered sentences below lost it with this comment.
 *
 * `deals` is the live count at this stage, or `null` when it could not be
 * fetched — the warning still fires but never claims a number. Never worded
 * as "no deals are affected" on a failed lookup.
 */
function ProbabilityWarning({ from, to, deals }: { from: number; to: number; deals: number | null }) {
  return (
    <StageNote heading={`Changing this stage’s odds from ${from}% to ${to}% moves the forecast.`}>
      {deals === null
        ? 'Every deal here will be re-forecast on save.'
        : deals === 0
          ? 'No deal sits here today — it applies to whatever lands here next.'
          : `${deals} ${deals === 1 ? 'deal sits' : 'deals sit'} here and ${deals === 1 ? 'it' : 'they'} will be re-forecast on save.`}
    </StageNote>
  );
}

/**
 * Both terminal flags set at once.
 *
 * **WARNED, NOT REFUSED — deliberately.** `isWon` and `isLost` are independent
 * booleans and the contract says so in as many words: nothing in the API stops
 * both being true, and a client refusing what the server accepts is the one
 * thing validation here must never do. It is still almost certainly a mistake,
 * and it is invisible in two ticked boxes, so it is said out loud with the
 * consequence named rather than left for somebody to find in a pipeline report.
 */
function BothFlagsWarning() {
  return (
    <StageNote heading="This stage is marked both won and lost.">
      It saves as typed, but the pipeline will report it as won and never as lost.
    </StageNote>
  );
}

/**
 * The quote-sent flag set on a won or lost stage.
 *
 * Warned, not refused, for the same reason as {@link BothFlagsWarning}: the
 * flags are independent booleans and the client never refuses what the server
 * accepts. The consequence is named because it is invisible in the ticked
 * boxes: since 2026-10-18 raising or linking a quote moves the deal to the
 * quote-sent stage, so a closed one there would close deals on a quote.
 */
function QuoteSentClosedWarning() {
  return (
    <StageNote heading="This stage is marked quote sent and also won or lost.">
      Raising or linking a quote moves a deal here, so it would close the deal.
    </StageNote>
  );
}

/**
 * The quote-signed flag set together with won, lost or quote sent.
 *
 * Unlike {@link QuoteSentClosedWarning}, the server REFUSES this combination
 * (422 on `isQuoteSigned`, contract 2026-10-27), so this names why the save
 * will fail before it does. The client still sends it as typed: the server's
 * message lands on the checkbox through `PATHS.dealStage`.
 */
function QuoteSignedCombinedWarning() {
  return (
    <StageNote heading="This stage is marked quote signed and also won, lost or quote sent.">
      A quote signed stage cannot also be won, lost or quote sent, so this will not save.
    </StageNote>
  );
}

/* -------------------------------------------------------------------------
 * Component
 * --------------------------------------------------------------------- */

export function MasterFormModal({ open, kind, record, onClose, onSaved }: MasterFormModalProps) {
  const config = KINDS[kind];
  const isEdit = record !== null;

  const [form, setForm] = useState<FormState>(() => recordForm(kind, record));
  const [baseline, setBaseline] = useState<FormState>(() => recordForm(kind, record));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  /*
   * "Have the currencies settled yet", rather than a loading flag flipped from
   * inside the effect — setting state synchronously in an effect starts a second
   * render before the first has painted. Loading is derived from it instead.
   */
  const [currenciesSettled, setCurrenciesSettled] = useState(false);

  /*
   * Reopening must not show the previous row's values or the last attempt's
   * errors. Done in the render phase rather than in an effect, because an effect
   * paints the stale form for a frame first.
   */
  const resetKey = `${open ? 'open' : 'closed'}|${kind}|${record ? record.id : 'new'}`;
  const [appliedKey, setAppliedKey] = useState(resetKey);
  if (appliedKey !== resetKey) {
    setAppliedKey(resetKey);
    if (open) {
      setForm(recordForm(kind, record));
      setBaseline(recordForm(kind, record));
      setErrors({});
      setFormError(null);
      setSaving(false);
    }
  }

  /*
   * The country form needs the currency master to offer a picker. Fetched here
   * rather than passed in so the modal stays self-contained; `perPage: 250` is
   * this endpoint's ceiling and clears all 164 rows in one request, so there is
   * never a second page to chase.
   */
  const needsCurrencies = open && config.shape === 'country';
  useEffect(() => {
    if (!needsCurrencies) return;
    const controller = new AbortController();
    api
      .listCurrencies({ page: 1, perPage: 250 }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCurrencies(result.data);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setCurrencies([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setCurrenciesSettled(true);
      });
    return () => controller.abort();
  }, [needsCurrencies]);

  const currenciesLoading = needsCurrencies && !currenciesSettled;
  const options = useMemo(() => currencyOptions(currencies), [currencies]);

  /*
   * The city form needs the country master for its own picker.
   *
   * ⚠️ `perPage: 250`, not 200. `/countries` is the OUTLIER ceiling — the four
   * picklists cap at 200 and `/cities` itself at 200, but countries and
   * currencies go to 250, chosen to clear today's 195 rows with headroom.
   * Omitting the key altogether pages silently at 50 and hides three quarters
   * of the world; asking for 200 fits today and would start truncating the day
   * the directory passes that mark. 251 is a 422.
   *
   * `isActive` is deliberately absent — see `countryOptions`.
   */
  /** Gates both of the city form's pickers — the country list and the regions. */
  const needsCityPickers = open && config.shape === 'city';
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesSettled, setCountriesSettled] = useState(false);

  useEffect(() => {
    if (!needsCityPickers) return;
    const controller = new AbortController();
    api
      .listCountries({ page: 1, perPage: 250 }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCountries(result.data);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setCountries([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setCountriesSettled(true);
      });
    return () => controller.abort();
  }, [needsCityPickers]);

  const countriesLoading = needsCityPickers && !countriesSettled;
  const countryChoices = useMemo(() => countryOptions(countries), [countries]);

  /*
   * The region picker, and the one control on this form that MUST be filtered.
   *
   * `useReferenceOptions` pins `isActive: true` — not a nicety: migration 047
   * left seventeen of the twenty-two regions retired, and the city write-time
   * check on `regionId` is active-only (`Region 9 does not exist or is not
   * active`). An unfiltered picker would offer seventeen guaranteed 422s.
   *
   * `withStoredOption` is the other half of the fix (see `cityPatch`): a city
   * already pointing at a since-retired region keeps that value visible and
   * selected, labelled as no longer offered, rather than the picker rendering
   * blank and quietly reassigning it.
   *
   * ⚠️ This is SEED's Region of focus, never derived from the chosen country —
   * see `MasterRecordSeed.region`.
   */
  /*
   * Enabled on the SHAPE alone, not on `open` — the one place this form does
   * not gate a fetch behind the dialog being visible.
   *
   * `useReferenceOptions` seeds `loading` from `enabled` at mount and only
   * clears it when the request settles. This component mounts closed, so
   * gating on `open` would flip `enabled` true *after* mount, leaving
   * `loading` false while the request is in flight — reading as "no regions"
   * for a paint or two instead of "loading". Keying it to the shape starts the
   * fetch when the Cities list opens, so the five rows are ready by the time
   * the form appears.
   */
  const regions = useReferenceOptions('regions', config.shape === 'city');
  const regionChoices = useMemo(
    () => withStoredOption(regions.records, record?.region ?? null),
    [regions.records, record],
  );

  /*
   * The campaign-inclusion form needs the product catalogue for its "Delivered
   * as" picker. Fetched here rather than passed in, so the modal stays
   * self-contained — the same arrangement as the currency and country lists
   * above, and there is no shared product-options hook in `src/lib` to reuse.
   *
   * ⚠️ **`perPage: 100` is `/products`' OWN ceiling, and it is a different
   * number from the two fetches above.** Countries and currencies cap at 250;
   * the entity family — products included — caps at 100, and 101 is a 422. So
   * this is not a value to "harmonise" with its neighbours. There are ~31
   * products today, so one page is genuinely the whole catalogue; if it ever
   * passes 100 this picker starts hiding rows silently, and the fix is paging,
   * never a larger number, because there is no larger number to ask for.
   *
   * ⚠️ **No `active` filter, deliberately** — see `productOptions`.
   */
  const needsProducts = open && config.shape === 'inclusion';
  const [products, setProducts] = useState<Product[]>([]);
  const [productsSettled, setProductsSettled] = useState(false);

  useEffect(() => {
    if (!needsProducts) return;
    const controller = new AbortController();
    api
      .listProducts({ page: 1, perPage: 100 }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setProducts(result.data);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setProducts([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setProductsSettled(true);
      });
    return () => controller.abort();
  }, [needsProducts]);

  const productsLoading = needsProducts && !productsSettled;
  const productChoices = useMemo(
    () => productOptions(products, record?.deliveredAsProduct ?? null),
    [products, record],
  );

  /*
   * How many deals sit at the stage being edited — for the forecast warning
   * only.
   *
   * `perPage: 1` because only `meta.total` is read — pulling real rows would
   * pay for data this form never renders. `?stageId=` is a real server-side
   * filter on `/deals`, not one of the params this API strips silently.
   *
   * A failure leaves the count `null` and the warning still fires WITHOUT a
   * number — it must never fall back to 0, since "no deals are affected" is a
   * claim, and this is the one screen where getting that wrong moves a forecast.
   */
  const needsDealCount = open && config.shape === 'dealStage' && isEdit;
  const stageId = record?.id;
  /*
   * The count is stored WITH the stage it was counted for, and read back only
   * when the two agree. This modal is not remounted between rows — the same
   * component serves Edit on one stage and then Edit on the next — so a bare
   * number would still be the previous stage's until the new request landed,
   * and the warning would name a count belonging to a different row. Pairing
   * them makes the stale window render as "no number" instead of a wrong one.
   */
  const [dealCount, setDealCount] = useState<{ stageId: number; total: number } | null>(null);

  useEffect(() => {
    if (!needsDealCount || stageId === undefined) return;
    const controller = new AbortController();
    api
      .listDeals({ page: 1, perPage: 1, stageId }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setDealCount({ stageId, total: result.meta.total });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setDealCount(null);
      });
    return () => controller.abort();
  }, [needsDealCount, stageId]);

  const dealsAtStage =
    dealCount !== null && dealCount.stageId === stageId ? dealCount.total : null;

  /*
   * Derived from the payload itself, so "Save is enabled" and "we have something
   * to send" can never disagree. An empty PATCH is a 422.
   */
  const patch = useMemo(
    () => (isEdit ? buildPatch(kind, form, baseline) : null),
    [isEdit, kind, form, baseline],
  );
  const dirty = patch === null || Object.keys(patch).length > 0;

  function setField<K extends keyof FormState>(key: K, value: FormState[K], field: FormField) {
    setForm((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => {
      if (!(field in previous)) return previous;
      const next = { ...previous };
      delete next[field];
      return next;
    });
  }

  /**
   * The saved row, or `null` when there was nothing to send.
   *
   * Every one of these endpoints answers with an `Envelope<T>` carrying the
   * stored record, so the row is returned rather than discarded — a caller that
   * opened this form to fill a gap in its own picker needs the id the server
   * allocated, and re-reading the master to find it would be guessing which row
   * is new.
   */
  async function save(): Promise<MasterSavedRecord | null> {
    if (isEdit && record) {
      const body = patch ?? {};
      if (Object.keys(body).length === 0) return null;
      if (REFERENCE_KINDS.has(kind)) {
        const result = await api.updateReference(
          kind as ReferenceMasterKind,
          record.id,
          body as ReferenceUpdateInput,
        );
        return result.data;
      } else if (kind === 'dealTypes') {
        return (await api.updateDealType(record.id, body as DealTypeUpdateInput)).data;
      } else if (kind === 'leadSources') {
        // Structurally identical to a deal type's patch, and a different
        // endpoint: `/lead-sources` is top-level and shared, not deal-scoped.
        return (await api.updateLeadSource(record.id, body as DealTypeUpdateInput)).data;
      } else if (kind === 'currencies') {
        return (await api.updateCurrency(record.id, body as CurrencyUpdateInput)).data;
      } else if (kind === 'cities') {
        return (await api.updateCity(record.id, body as CityUpdateInput)).data;
      } else if (kind === 'campaignInclusions') {
        return (await api.updateCampaignInclusion(record.id, body as CampaignInclusionUpdateInput))
          .data;
      } else if (kind === 'dealStages') {
        return (await api.updateDealStage(record.id, body as DealStageUpdateInput)).data;
      } else {
        return (await api.updateCountry(record.id, body as CountryUpdateInput)).data;
      }
    }

    if (REFERENCE_KINDS.has(kind)) {
      return (await api.createReference(kind as ReferenceMasterKind, referenceCreate(kind, form))).data;
    } else if (kind === 'dealTypes') {
      return (await api.createDealType(pickerCreate(form))).data;
    } else if (kind === 'leadSources') {
      return (await api.createLeadSource(pickerCreate(form))).data;
    } else if (kind === 'currencies') {
      return (await api.createCurrency(currencyCreate(form))).data;
    } else if (kind === 'cities') {
      return (await api.createCity(cityCreate(form))).data;
    } else if (kind === 'campaignInclusions') {
      return (await api.createCampaignInclusion(inclusionCreate(form))).data;
    } else if (kind === 'dealStages') {
      return (await api.createDealStage(dealStageCreate(form))).data;
    } else {
      return (await api.createCountry(countryCreate(form))).data;
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    /*
     * ⚠️ **This form can be opened from inside another one**, and React
     * propagates events by the REACT tree rather than the DOM one — the portal
     * puts this dialog on `document.body`, but it is still a child of whatever
     * rendered it. Without this, submitting "New city" from inside the project
     * form ALSO submits the project behind it, creating a half-filled record
     * the user never asked for. `tsc` and oxlint were both green while exactly
     * that shipped from `PickerField`; see the note on its own `handleSubmit`.
     *
     * Harmless where this form is mounted on its own, as on `/catalog`: there
     * is no form above it for the event to reach.
     */
    event.stopPropagation();
    if (saving) return;

    const clientErrors = validate(kind, form);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setFormError(null);
      return;
    }

    setSaving(true);
    setErrors({});
    setFormError(null);

    try {
      const saved = await save();
      onSaved(saved);
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        /*
         * A duplicate is a 409 and carries no `details`, so it has to be routed
         * to its field by hand. The server's own message names the clash, so it
         * is preferred over anything written here.
         */
        if (error.code === 'REFERENCE_DEFAULT_REQUIRED') {
          // Price tiers: the write would leave no default tier (clear / retire /
          // inactive default). Not a name clash, so it is not routed to `name`.
          setErrors({});
          setFormError(error.message);
        } else if (error.status === 409) {
          setErrors({ [CONFLICT_FIELD[config.shape]]: error.message });
          setFormError(null);
        } else if (error.status === 403) {
          setFormError(
            `Only an administrator can change the ${config.noun} list. Your account can view it but not edit it.`,
          );
        } else if (error.status === 404) {
          setFormError(`This ${config.noun} no longer exists. Close this form and reload the list.`);
        } else {
          const mapped: FieldErrors = {};
          /*
           * Seeded with the messages that belong to no field. A `.strict()`
           * rejection names the offending key with an EMPTY path, so it can
           * only ever be shown at form level — and every write schema in the
           * catalog module is strict.
           */
          const unmapped: string[] = [...error.formMessages];
          for (const [path, message] of Object.entries(error.fieldErrors)) {
            const field = PATHS[config.shape][path];
            if (field) mapped[field] = message;
            else unmapped.push(`${path}: ${message}`);
          }
          setErrors(mapped);
          if (unmapped.length > 0) setFormError(`${error.message} — ${unmapped.join('; ')}`);
          else if (Object.keys(mapped).length === 0) setFormError(error.message);
          else setFormError(null);
        }
      } else {
        setFormError('Could not reach the server. Check your connection.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  const formId = `master-form-${kind}`;
  const title = isEdit ? `Edit ${config.noun}` : `New ${config.noun}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={titleCase(title)}
      // A half-typed form must not vanish on a clipped backdrop click.
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={saving}
            // An empty PATCH is a 422, so there is nothing to send and nothing to do.
            disabled={!dirty}
          >
            {isEdit ? 'Save changes' : `Create ${config.noun}`}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="grid gap-4">
        {formError && (
          <Notice tone="danger" role="alert">
            <span className="[overflow-wrap:anywhere]">{formError}</span>
          </Notice>
        )}

        <Field
          label={config.nameLabel}
          required
          error={errors.name}
        >
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={form.name}
              maxLength={config.nameMax ?? undefined}
              autoComplete="off"
              invalid={Boolean(errors.name)}
              onChange={(event) => setField('name', event.target.value, 'name')}
            />
          )}
        </Field>

        {config.shape === 'reference' && (
          <Field
            label="Sort order"
            error={errors.sortOrder}
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="number"
                inputMode="numeric"
                min={0}
                max={SORT_ORDER_MAX}
                step={1}
                value={form.sortOrder}
                invalid={Boolean(errors.sortOrder)}
                onChange={(event) => setField('sortOrder', event.target.value, 'sortOrder')}
              />
            )}
          </Field>
        )}

        {/*
          * Same control as the masters' above, but the range comes from config
          * (signed `INT`, negatives legal) and the deal pickers order
          * `sortOrder ASC, id ASC` — a different tie-break from the masters'.
          */}
        {config.shape === 'picker' && (
          <Field
            label="Sort order"
            error={errors.sortOrder}
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="number"
                inputMode="numeric"
                min={config.sortOrderMin ?? 0}
                max={config.sortOrderMax ?? SORT_ORDER_MAX}
                step={1}
                value={form.sortOrder}
                invalid={Boolean(errors.sortOrder)}
                onChange={(event) => setField('sortOrder', event.target.value, 'sortOrder')}
              />
            )}
          </Field>
        )}

        {config.shape === 'inclusion' && (
          <>
            {/*
              * Two boxes, filled independently — nothing prefills one from the
              * other. See `inclusionCreate` for why ("campus" → "campuses"
              * follows no derivable rule).
              */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Unit for exactly one"
                error={errors.unitSingular}
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={form.unitSingular}
                    maxLength={UNIT_MAX}
                    autoComplete="off"
                    invalid={Boolean(errors.unitSingular)}
                    onChange={(event) =>
                      setField('unitSingular', event.target.value, 'unitSingular')
                    }
                  />
                )}
              </Field>

              <Field
                label="Unit for any other quantity"
                error={errors.unitPlural}
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={form.unitPlural}
                    maxLength={UNIT_MAX}
                    autoComplete="off"
                    invalid={Boolean(errors.unitPlural)}
                    onChange={(event) => setField('unitPlural', event.target.value, 'unitPlural')}
                  />
                )}
              </Field>
            </div>

            <UnitPreview
              name={form.name}
              unitSingular={form.unitSingular}
              unitPlural={form.unitPlural}
            />

            {/*
              * NOT A LABEL — IT CHANGES WHAT THE TRACKER SHOWS.
              *
              * Ticked, every quote or invoice line carrying this inclusion
              * produces its OWN row on `/client-deliverables`, for every
              * reader, across documents that already exist; unticked, that
              * population leaves again. The two boxes above decide how an
              * inclusion is WORDED on a customer's document. This one decides
              * how many rows somebody plans delivery against, which is why it
              * sits under the preview rather than beside them.
              *
              * ⚠️ It is a FLAG rather than a name match or an id, and that is
              * MEASURED, not a preference. Inclusion id 4 was seeded as
              * "Profile Evaluation" by migration 056 and an admin has since
              * RENAMED it to "Meet-Up" in production — so the name moved out
              * from under the behaviour, exactly as the three substring-matched
              * status names elsewhere in this codebase did. An id is no better:
              * it pins behaviour to production's own counter, which the
              * migration set does not reproduce.
              */}
            <Checkbox
              checked={form.trackedAsDeliverable}
              onChange={(event) =>
                setField('trackedAsDeliverable', event.target.checked, 'trackedAsDeliverable')
              }
              label="Track as its own deliverable"
            />

            {/*
              * WHAT THIS INCLUSION **IS** WHEN IT IS DELIVERED — so a row the
              * tickbox above spawns can be attributed to a catalogue product.
              * The owner confirmed the inclusion "Meet-Up" and the product
              * "Meet Up" are the same thing; nothing in the data said so, and
              * this picker is where somebody says it.
              *
              * ⛔ **THIS IS NOT THE PER-PRODUCT "INCLUSIONS" LINK TABLE**
              * (`f_product_inclusions`, the row action of that name on
              * `/products`, `GET | PUT /products/{id}/inclusions`). That table
              * runs the OTHER WAY and answers a different question: which
              * products OFFER this inclusion. Meet-Up is offered by *Virtual
              * Masterclasses* — the only product offering anything today — so
              * attributing through it would file every Meet-Up deliverable
              * under Virtual Masterclasses, which is the product it was SOLD
              * WITH and not the product it IS. One inclusion may be offered by
              * many products and still be exactly one thing when delivered, so
              * the two can never be derived from each other in either
              * direction. ⚠️ A reader who has just used that row action will
              * recognise this control and be wrong about it; a `200` either way
              * and a tracker quietly reporting the wrong product.
              *
              * ⚠️ **ENABLED EVEN WHEN THE TICKBOX ABOVE IS OFF — decided, not
              * defaulted.** The mapping only pays off once rows exist, so
              * disabling it while untracked is the tempting move. Three reasons
              * it is not taken:
              *
              * - A bare `disabled` makes a derived value, a permission denial
              *   and an ordinary empty box identical, so doing it honestly
              *   needs a `denialReason`-style sentence beside the control — and
              *   that sentence is exactly the helper text this form is not
              *   allowed to grow. The only correct way to disable it is one the
              *   house rules forbid.
              * - It would impose an order on two settings that save in ONE
              *   PATCH: pick first and the choice is dropped by an inert
              *   control, tick first and it works. Nothing else on this form
              *   sequences two fields, and a control that silently ignores
              *   input is the failure a disabled Save already taught this
              *   codebase once.
              * - A mapping set while untracked is HARMLESS and DURABLE. It
              *   attributes nothing today and is already correct the day
              *   somebody ticks the box — which is the useful direction, since
              *   the person who knows Meet-Up is Meet Up is not necessarily the
              *   person deciding to track it.
              *
              * Unticking the box likewise does NOT clear this — see
              * `inclusionPatch`.
              */}
            <Field label="Delivered as this catalogue product" error={errors.deliveredAsProductId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={form.deliveredAsProductId}
                  onChange={(value) =>
                    setField('deliveredAsProductId', value, 'deliveredAsProductId')
                  }
                  options={productChoices}
                  /*
                   * The empty row says what choosing it MEANS, not that nothing
                   * is chosen. "None" would read as an unfinished field, and
                   * four of the five live inclusions are legitimately in this
                   * state — it is the ordinary answer, not a gap.
                   */
                  placeholder="Not a catalogue product"
                  emptyOptionLabel="Not a catalogue product"
                  searchPlaceholder="Search products…"
                  loading={productsLoading && productChoices.length === 0}
                  invalid={Boolean(errors.deliveredAsProductId)}
                />
              )}
            </Field>

            <Field
              label="Sort order"
              error={errors.sortOrder}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={config.sortOrderMax ?? SORT_ORDER_MAX}
                  step={1}
                  value={form.sortOrder}
                  invalid={Boolean(errors.sortOrder)}
                  onChange={(event) => setField('sortOrder', event.target.value, 'sortOrder')}
                />
              )}
            </Field>
          </>
        )}

        {config.shape === 'dealStage' && (
          <>
            {/*
              * Text box first, colour well second — deliberately. A well can't
              * be typed into (a pasted `#1e88e5` needs the text box) and has no
              * empty state (it reports `#000000` when unset, so "no colour"
              * would become unsayable). The well shows a FALLBACK when the box
              * holds nothing usable; that fallback is never written or sent.
              * Clear is the only way back to no-colour once the well is touched.
              */}
            <Field
              label="Colour"
              error={errors.colour}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={form.colour}
                    autoComplete="off"
                    spellCheck={false}
                      className="w-36 tabular"
                    invalid={Boolean(errors.colour)}
                    onChange={(event) => setField('colour', event.target.value, 'colour')}
                  />

                  {/*
                    * Native — no design-system primitive for a colour well, and
                    * inventing one would be worse than the platform's. It's a
                    * SECOND way to set the box, so it gets its own label.
                    */}
                  <input
                    type="color"
                    aria-label="Pick a colour"
                    className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface p-1 max-sm:h-11"
                    value={
                      HEX_COLOUR.test(form.colour.trim())
                        ? form.colour.trim().toLowerCase()
                        : COLOUR_WELL_FALLBACK
                    }
                    onChange={(event) => setField('colour', event.target.value, 'colour')}
                  />

                  {/*
                    * The preview renders NOTHING when the hex is unusable —
                    * `StageSwatch` is the app's one validated painter and
                    * refuses anything but `#rrggbb`. A grey or black square
                    * would read as a colour the stage does not have, which is
                    * the exact confusion the empty state exists to avoid.
                    */}
                  <span className="flex items-center gap-1.5 text-xs text-fg-muted">
                    <StageSwatch colour={form.colour.trim()} />
                    {HEX_COLOUR.test(form.colour.trim())
                      ? 'Preview'
                      : form.colour.trim()
                        ? 'Not a usable hex code'
                        : 'No colour'}
                  </span>

                  {form.colour.trim() !== '' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setField('colour', '', 'colour')}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              )}
            </Field>

            <Field
              label="Probability"
              error={errors.probability}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={PROBABILITY_MAX}
                  step={1}
                  className="w-28"
                  value={form.probability}
                  invalid={Boolean(errors.probability)}
                  onChange={(event) => setField('probability', event.target.value, 'probability')}
                />
              )}
            </Field>

            {/*
              * Fired on the DIFF, not on the value, and only on an edit: a new
              * stage has no deals on it and nothing to re-forecast, so the
              * warning would be noise on the create form and would train people
              * to ignore it where it matters.
              */}
            {isEdit &&
              form.probability.trim() !== '' &&
              Number(form.probability) !== Number(baseline.probability) && (
                <ProbabilityWarning
                  from={Number(baseline.probability)}
                  to={Number(form.probability)}
                  deals={dealsAtStage}
                />
              )}

            {/*
              * TWO INDEPENDENT FLAGS, NOT ONE OUTCOME PICKER.
              *
              * A radio group of Open / Won / Lost would be the obvious control
              * and would be wrong: the API stores two booleans, allows both and
              * allows neither, and a picker that cannot express both-set would
              * quietly rewrite a row somebody already has. There is also no
              * "Closed Won" in this pipeline — `Invoiced` is the won terminal —
              * so nothing here may invent an outcome vocabulary of its own.
              */}
            <div className="grid gap-3">
              <Checkbox
                checked={form.isWon}
                onChange={(event) => setField('isWon', event.target.checked, 'isWon')}
                label="Won — closes the deal as won"
              />
              <Checkbox
                checked={form.isLost}
                onChange={(event) => setField('isLost', event.target.checked, 'isLost')}
                label="Lost — closes the deal as lost"
              />
              <Checkbox
                checked={form.isQuoteSent}
                onChange={(event) =>
                  setField('isQuoteSent', event.target.checked, 'isQuoteSent')
                }
                label="Quote sent stage"
              />
              <Checkbox
                checked={form.isQuoteSigned}
                onChange={(event) =>
                  setField('isQuoteSigned', event.target.checked, 'isQuoteSigned')
                }
                label="Quote signed stage"
              />
            </div>

            {form.isWon && form.isLost && <BothFlagsWarning />}
            {form.isQuoteSent && (form.isWon || form.isLost) && <QuoteSentClosedWarning />}
            {form.isQuoteSigned && (form.isWon || form.isLost || form.isQuoteSent) && (
              <QuoteSignedCombinedWarning />
            )}

            <Field
              label="Pipeline order"
              error={errors.sortOrder}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="number"
                  inputMode="numeric"
                  min={config.sortOrderMin ?? 0}
                  max={config.sortOrderMax ?? SORT_ORDER_MAX}
                  step={1}
                  className="w-36"
                  value={form.sortOrder}
                  invalid={Boolean(errors.sortOrder)}
                  onChange={(event) => setField('sortOrder', event.target.value, 'sortOrder')}
                />
              )}
            </Field>
          </>
        )}

        {config.shape === 'currency' && (
          <Field
            label="Currency code"
            required
            error={errors.code}
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={form.code}
                maxLength={3}
                autoComplete="off"
                spellCheck={false}
                className="uppercase"
                invalid={Boolean(errors.code)}
                onChange={(event) =>
                  setField('code', event.target.value.toUpperCase(), 'code')
                }
              />
            )}
          </Field>
        )}

        {config.shape === 'city' && (
          <>
            <Field label="Country" error={errors.countryId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={form.countryId}
                  onChange={(value) => setField('countryId', value, 'countryId')}
                  options={countryChoices}
                  placeholder="No country"
                  emptyOptionLabel="No country"
                  searchPlaceholder="Search countries…"
                  loading={countriesLoading && countryChoices.length === 0}
                  invalid={Boolean(errors.countryId)}
                />
              )}
            </Field>

            {/*
              * Region is picked, never derived from country — see the region
              * picker comment above `useReferenceOptions('regions', ...)` for
              * why (SEED's Region of focus vs a country's own unrelated
              * `regionId`). Only ACTIVE regions are offered: the server refuses
              * a retired one even on a write that didn't change it.
              */}
            <Field label="Region of focus" error={errors.regionId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={form.regionId}
                  onChange={(value) => setField('regionId', value, 'regionId')}
                  options={regionChoices}
                  placeholder="Unclassified"
                  emptyOptionLabel="Unclassified"
                  searchPlaceholder="Search regions…"
                  loading={regions.loading && regionChoices.length === 0}
                  invalid={Boolean(errors.regionId)}
                />
              )}
            </Field>

            <Field
              label="Sort order"
              error={errors.sortOrder}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={config.sortOrderMax ?? SORT_ORDER_MAX}
                  step={1}
                  value={form.sortOrder}
                  invalid={Boolean(errors.sortOrder)}
                  onChange={(event) => setField('sortOrder', event.target.value, 'sortOrder')}
                />
              )}
            </Field>
          </>
        )}

        {config.shape === 'country' && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Country code"
                required
                error={errors.code}
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={form.code}
                    maxLength={2}
                    autoComplete="off"
                    spellCheck={false}
                    className="uppercase"
                    invalid={Boolean(errors.code)}
                    onChange={(event) =>
                      setField('code', event.target.value.toUpperCase(), 'code')
                    }
                  />
                )}
              </Field>

              <Field
                label="Flag code"
                required
                error={errors.flagCode}
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={form.flagCode}
                    maxLength={2}
                    autoComplete="off"
                    spellCheck={false}
                    className="uppercase"
                    invalid={Boolean(errors.flagCode)}
                    onChange={(event) =>
                      setField('flagCode', event.target.value.toUpperCase(), 'flagCode')
                    }
                  />
                )}
              </Field>
            </div>

            <Field
              label="Dialling code"
              required
              error={errors.callingCode}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={form.callingCode}
                  maxLength={8}
                  autoComplete="off"
                  invalid={Boolean(errors.callingCode)}
                  onChange={(event) => setField('callingCode', event.target.value, 'callingCode')}
                />
              )}
            </Field>

            <Field label="Currency" error={errors.currencyId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={form.currencyId}
                  onChange={(value) => setField('currencyId', value, 'currencyId')}
                  options={options}
                  placeholder="No currency"
                  emptyOptionLabel="No currency"
                  searchPlaceholder="Search currencies…"
                  loading={currenciesLoading && options.length === 0}
                  invalid={Boolean(errors.currencyId)}
                />
              )}
            </Field>

            {/*
              * Continent and region: shown, never edited — no endpoint exists to
              * populate a picker, and a free-text number box would be meaningless
              * and error-prone. This `regionId` is NOT SEED's "Region of focus";
              * it's an unrelated upstream id sequence (see the region-picker note
              * on the Cities form) — joining the two produces plausible nonsense,
              * not an error.
              */}
            {isEdit && (
              <div className="rounded-md border border-border bg-surface-sunken px-3 py-2.5">
                <p className="m-0 text-xs font-medium text-fg-secondary">Upstream geography</p>
                <dl className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1 text-xs text-fg-muted">
                  <div className="flex gap-1.5">
                    <dt>Continent id</dt>
                    <dd className="m-0 tabular text-fg-secondary">
                      {record?.continentId ?? '—'}
                    </dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt>Region id</dt>
                    <dd className="m-0 tabular text-fg-secondary">{record?.regionId ?? '—'}</dd>
                  </div>
                </dl>
              </div>
            )}

            <Checkbox
              checked={form.popular}
              onChange={(event) => setField('popular', event.target.checked, 'popular')}
              label="Popular study destination"
            />
          </>
        )}

        {kind === 'companyTypes' && (
          <Checkbox
            checked={form.isPartner}
            onChange={(event) => setField('isPartner', event.target.checked, 'isPartner')}
            label="Partner category"
          />
        )}

        {kind === 'priceTiers' && (
          <Checkbox
            checked={form.isDefault}
            onChange={(event) => setField('isDefault', event.target.checked, 'isDefault')}
            label="Default tier"
          />
        )}

        <Checkbox
          checked={form.isActive}
          onChange={(event) => setField('isActive', event.target.checked, 'isActive')}
          label="Active"
        />
      </form>
    </Modal>
  );
}
